import React from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowRight,
  BadgeCheck,
  BookOpen,
  Briefcase,
  CheckCircle2,
  Compass,
  GraduationCap,
  Laptop,
  MapPin,
  Search,
  Target,
  Users,
  type LucideIcon,
} from 'lucide-react';
import SEO from '../../components/SEO/SEO';

const SITE_URL = 'https://localally.in';

type Faq = {
  question: string;
  answer: string;
};

type LinkItem = {
  label: string;
  to: string;
  description: string;
};

type SubjectPage = {
  slug: string;
  name: string;
  title: string;
  description: string;
  h1: string;
  summary: string;
  learningNeeds: string[];
  usefulFor: string[];
  faqs: Faq[];
};

type ClassPage = {
  slug: string;
  name: string;
  title: string;
  description: string;
  h1: string;
  summary: string;
  subjects: string[];
  parentGuidance: string[];
  faqs: Faq[];
};

type BoardPage = {
  slug: string;
  name: string;
  title: string;
  description: string;
  summary: string;
  support: string[];
  faqs: Faq[];
};

type ExamPage = {
  slug: string;
  name: string;
  title: string;
  description: string;
  summary: string;
  subjects: string[];
  coachingModes: string[];
  faqs: Faq[];
};

type NearbyAreaPage = {
  slug: string;
  name: string;
  region: string;
  title: string;
  description: string;
  summary: string;
  localSignals: string[];
  faqs: Faq[];
};

const RADIUS_OPTIONS = [
  {
    label: '5 km nearby search',
    description: 'Start with tutors, mentors and coaching support close to the student.',
  },
  {
    label: '10 km expanded search',
    description: 'Expand when the exact subject, board or exam support is not available nearby.',
  },
  {
    label: '20 km wider search',
    description: 'Use a wider radius for competitive exams, specialist tutors and coaching providers.',
  },
] as const;

const EDUCATION_CATEGORIES: LinkItem[] = [
  {
    label: 'All classes',
    to: '/education/classes',
    description: 'Nursery, LKG, UKG, Class 1 to Class 12, intermediate and skill-based learning support.',
  },
  {
    label: 'All subjects',
    to: '/education/tutors/maths',
    description: 'Maths, Science, English, Social Studies, Computer, spoken English and more as tutors join.',
  },
  {
    label: 'Boards',
    to: '/education/boards/cbse',
    description: 'CBSE, State Board, ICSE and local board support where suitable tutors are available.',
  },
  {
    label: 'Competitive exams',
    to: '/education/competitive-exams',
    description: 'NEET, JEE, EAMCET, UPSC, APPSC, TGPSC, bank, SSC, railway, TET and DSC coaching discovery.',
  },
  {
    label: 'Books & Stationery',
    to: '/books',
    description: 'Buy, sell, exchange, donate, request books from shops, and support verified local book shops.',
  },
];

const SUBJECT_PAGES: Record<string, SubjectPage> = {
  maths: {
    slug: 'maths',
    name: 'Maths',
    title: 'Maths Tutors Near You for All Classes - LocalAlly',
    description:
      'Find Maths tutors near you for all classes, board exams, foundation maths and competitive exam quantitative aptitude on LocalAlly.',
    h1: 'Find Maths Tutors Near You',
    summary:
      'LocalAlly helps students and parents find Maths tutors for school basics, chapter-wise practice, board preparation and quantitative aptitude. Start with nearby discovery and expand the search radius when a specialist is needed.',
    learningNeeds: ['Foundation maths', 'Chapter-wise practice', 'Board exam maths', 'Quantitative aptitude'],
    usefulFor: ['Primary classes', 'Middle school', 'High school', 'Competitive exam aspirants'],
    faqs: [
      {
        question: 'Can LocalAlly help with Maths tutors for all classes?',
        answer:
          'Yes. LocalAlly is positioned for all classes and learning levels, not only Class 9 or Class 10.',
      },
      {
        question: 'Can I search for Maths tutors near my area?',
        answer:
          'Yes. Students can use nearby search language around 5 km, 10 km and 20 km depending on tutor availability.',
      },
    ],
  },
  science: {
    slug: 'science',
    name: 'Science',
    title: 'Science Tutors Near You for School and Exam Support - LocalAlly',
    description:
      'Find nearby Science tutors for school classes, Physics, Chemistry, Biology, board exams and competitive exam foundations.',
    h1: 'Find Science Tutors Near You',
    summary:
      'Science support on LocalAlly covers school concepts, diagrams, definitions, lab awareness, board exam revision and foundations for entrance exams. Parents can begin with their area and widen the search as needed.',
    learningNeeds: ['Physics basics', 'Chemistry concepts', 'Biology learning', 'Science exam revision'],
    usefulFor: ['School students', 'Board exam learners', 'NEET foundation students', 'Curious learners'],
    faqs: [
      {
        question: 'Does LocalAlly cover Physics, Chemistry and Biology?',
        answer:
          'Yes. Science pages are written for school science and exam-oriented support across Physics, Chemistry and Biology where tutors are available.',
      },
      {
        question: 'Is Science support only for board classes?',
        answer:
          'No. LocalAlly supports all school levels and can also connect students with exam foundation support.',
      },
    ],
  },
  english: {
    slug: 'english',
    name: 'English',
    title: 'English Tutors Near You for School, Spoken English and Exams - LocalAlly',
    description:
      'Find English tutors near you for grammar, reading, writing, spoken English, school exams and communication skills.',
    h1: 'Find English Tutors Near You',
    summary:
      'English tuition can support school grammar, reading comprehension, writing practice, literature and spoken confidence. LocalAlly keeps the path simple for students, parents and tutors.',
    learningNeeds: ['Grammar practice', 'Reading comprehension', 'Writing skills', 'Spoken English'],
    usefulFor: ['School students', 'Interview preparation', 'Communication practice', 'Exam revision'],
    faqs: [
      {
        question: 'Can students find spoken English tutors on LocalAlly?',
        answer:
          'Yes. Spoken English and communication skills are part of the broader LocalAlly education scope.',
      },
      {
        question: 'Does English support include school exams?',
        answer:
          'Yes. Students can look for English support for grammar, writing, comprehension and exam preparation.',
      },
    ],
  },
  'social-studies': {
    slug: 'social-studies',
    name: 'Social Studies',
    title: 'Social Studies Tutors Near You - LocalAlly',
    description:
      'Find Social Studies tutors near you for history, geography, civics, economics, school tests and board exam revision.',
    h1: 'Find Social Studies Tutors Near You',
    summary:
      'Social Studies support helps students understand history, geography, civics and economics with better notes, maps, timelines and answer writing.',
    learningNeeds: ['History timelines', 'Geography maps', 'Civics concepts', 'Economics basics'],
    usefulFor: ['Middle school', 'High school', 'Board exam students', 'Answer-writing practice'],
    faqs: [
      {
        question: 'Can LocalAlly support Social Studies tutor searches?',
        answer:
          'Yes. Social Studies is part of the subject SEO structure for school students and board preparation.',
      },
      {
        question: 'Is Social Studies useful for competitive exam basics?',
        answer:
          'Yes. Strong school-level Social Studies can support later general studies preparation.',
      },
    ],
  },
  computer: {
    slug: 'computer',
    name: 'Computer',
    title: 'Computer and Coding Tutors Near You - LocalAlly',
    description:
      'Find Computer tutors near you for computer basics, coding, school computer science and career skill learning.',
    h1: 'Find Computer and Coding Tutors Near You',
    summary:
      'Computer learning on LocalAlly can include school computer science, digital basics, coding foundations and career-oriented skills when suitable tutors are available.',
    learningNeeds: ['Computer basics', 'School computer science', 'Coding foundations', 'Career skills'],
    usefulFor: ['School students', 'Beginners', 'Skill learners', 'Coding starters'],
    faqs: [
      {
        question: 'Can students search for coding tutors?',
        answer:
          'Yes. Coding and computer basics are included in LocalAlly education discovery where tutors are available.',
      },
      {
        question: 'Is Computer only for school syllabus?',
        answer:
          'No. The page also supports skill-based learning and beginner-friendly computer help.',
      },
    ],
  },
};

const BOARD_PAGES: Record<string, BoardPage> = {
  cbse: {
    slug: 'cbse',
    name: 'CBSE',
    title: 'CBSE Tutors Near You - LocalAlly',
    description:
      'Find CBSE tutors near you for all classes, subjects, school exams, board preparation and regular tuition support.',
    summary:
      'LocalAlly helps CBSE students and parents discover nearby tutors for class-wise subjects, homework support, revision and board-focused preparation.',
    support: ['NCERT-aligned concepts', 'Class-wise subjects', 'Board exam revision', 'Regular assessment support'],
    faqs: [
      {
        question: 'Can LocalAlly help find CBSE tutors nearby?',
        answer:
          'Yes. CBSE support is included in the board-specific SEO structure, with nearby search language around 5 km, 10 km and 20 km.',
      },
      {
        question: 'Is CBSE support only for Class 10?',
        answer:
          'No. LocalAlly supports CBSE learning across school classes where tutors are available.',
      },
    ],
  },
  'state-board': {
    slug: 'state-board',
    name: 'State Board',
    title: 'State Board Tutors Near You - LocalAlly',
    description:
      'Find State Board tutors near you for AP, Telangana and other state board school subjects and exam preparation.',
    summary:
      'State Board students can use LocalAlly to search for local tutors who understand school syllabus needs, language preferences, regular tests and board exam preparation.',
    support: ['AP board support', 'Telangana board support', 'Local syllabus guidance', 'School test preparation'],
    faqs: [
      {
        question: 'Does LocalAlly support AP and Telangana State Board students?',
        answer:
          'Yes. State Board support includes AP, Telangana and other local board needs where tutors are available.',
      },
      {
        question: 'Can students search by area or pincode?',
        answer:
          'The SEO pages explain nearby discovery by area, pincode and radius so users can search locally inside the app flow.',
      },
    ],
  },
  icse: {
    slug: 'icse',
    name: 'ICSE',
    title: 'ICSE Tutors Near You - LocalAlly',
    description:
      'Find ICSE tutors near you for school subjects, structured practice, exam preparation and class-wise tuition support.',
    summary:
      'ICSE learners often need clear concept explanation, writing practice and consistent revision. LocalAlly keeps ICSE discovery connected to nearby tutor search.',
    support: ['Class-wise ICSE subjects', 'Detailed answer writing', 'Revision support', 'Parent-guided tutor search'],
    faqs: [
      {
        question: 'Can ICSE students use LocalAlly?',
        answer:
          'Yes. ICSE is included in the broader board support plan when relevant tutors are available.',
      },
      {
        question: 'Can ICSE tutors register?',
        answer:
          'Yes. Tutors can register and mention boards, subjects, classes and preferred teaching radius.',
      },
    ],
  },
};

const EXAM_PAGES: Record<string, ExamPage> = {
  neet: {
    slug: 'neet',
    name: 'NEET',
    title: 'NEET Coaching Near You - LocalAlly',
    description:
      'Discover nearby NEET coaching and tutors for Biology, Physics, Chemistry, test practice and entrance exam preparation.',
    summary:
      'LocalAlly helps NEET aspirants search for nearby coaching support, subject mentors and test-preparation guidance without limiting discovery to only one city.',
    subjects: ['Biology', 'Physics', 'Chemistry', 'Test practice'],
    coachingModes: ['Home tuition', 'Group coaching', 'Online support', 'Mentor guidance'],
    faqs: [
      {
        question: 'Can LocalAlly help with NEET coaching searches?',
        answer:
          'Yes. NEET is part of the competitive exam coaching discovery scope for nearby tutors and coaching providers.',
      },
      {
        question: 'Can NEET coaching providers register?',
        answer:
          'Yes. Coaching providers and subject mentors can use tutor registration to join LocalAlly.',
      },
    ],
  },
  jee: {
    slug: 'jee',
    name: 'JEE',
    title: 'JEE Coaching Near You - LocalAlly',
    description:
      'Find JEE coaching near you for Maths, Physics, Chemistry, foundation support and entrance exam practice.',
    summary:
      'JEE preparation needs strong concepts and consistent problem solving. LocalAlly connects JEE search intent with nearby tutor and coaching discovery.',
    subjects: ['Maths', 'Physics', 'Chemistry', 'Problem solving'],
    coachingModes: ['One-to-one tutoring', 'Group batches', 'Online classes', 'Practice mentoring'],
    faqs: [
      {
        question: 'Does LocalAlly support JEE coaching discovery?',
        answer:
          'Yes. JEE coaching is included in the competitive exam SEO pages and registration paths.',
      },
      {
        question: 'Can students search for JEE support within 20 km?',
        answer:
          'Yes. The nearby discovery model uses 5 km, 10 km and 20 km radius language depending on availability.',
      },
    ],
  },
  eamcet: {
    slug: 'eamcet',
    name: 'EAMCET',
    title: 'EAMCET Coaching Near You - LocalAlly',
    description:
      'Find EAMCET coaching near you for Maths, Physics, Chemistry, Biology, entrance practice and local mentor support.',
    summary:
      'EAMCET students can use LocalAlly to discover local coaching support for AP and Telangana entrance preparation across relevant subjects.',
    subjects: ['Maths', 'Physics', 'Chemistry', 'Biology'],
    coachingModes: ['Local coaching', 'Home tuition', 'Online support', 'Test practice'],
    faqs: [
      {
        question: 'Does LocalAlly cover EAMCET coaching?',
        answer:
          'Yes. EAMCET is part of the competitive exam discovery structure for nearby coaching support.',
      },
      {
        question: 'Is EAMCET only for city pages?',
        answer:
          'No. LocalAlly is not city-only; it is designed around nearby search by area, pincode and radius.',
      },
    ],
  },
  upsc: {
    slug: 'upsc',
    name: 'UPSC',
    title: 'UPSC Coaching Near You - LocalAlly',
    description:
      'Find UPSC coaching near you for general studies, optional support, current affairs and exam mentoring on LocalAlly.',
    summary:
      'UPSC aspirants can discover nearby mentors, coaching providers and subject support while keeping the search flexible across area and radius.',
    subjects: ['General studies', 'Current affairs', 'Optional guidance', 'Answer writing'],
    coachingModes: ['Mentoring', 'Group coaching', 'Online classes', 'Test series support'],
    faqs: [
      {
        question: 'Can UPSC mentors register on LocalAlly?',
        answer:
          'Yes. Mentors and coaching providers can register and list exam categories they support.',
      },
      {
        question: 'Can aspirants search beyond 5 km?',
        answer:
          'Yes. Aspirants can expand discovery to 10 km or 20 km when specialist support is needed.',
      },
    ],
  },
  appsc: {
    slug: 'appsc',
    name: 'APPSC',
    title: 'APPSC Coaching Near You - LocalAlly',
    description:
      'Find APPSC coaching near you for general studies, state-specific preparation, current affairs and exam practice.',
    summary:
      'APPSC preparation often needs local state awareness and structured practice. LocalAlly supports nearby discovery for aspirants and coaching providers.',
    subjects: ['General studies', 'AP state topics', 'Current affairs', 'Practice tests'],
    coachingModes: ['Local coaching', 'Mentor support', 'Online classes', 'Group batches'],
    faqs: [
      {
        question: 'Does LocalAlly include APPSC coaching?',
        answer:
          'Yes. APPSC is included in the competitive exam pages for Andhra Pradesh-focused aspirants.',
      },
      {
        question: 'Can APPSC tutors list their support areas?',
        answer:
          'Yes. Tutors and coaching providers should list area, radius, exam categories and teaching mode.',
      },
    ],
  },
  tgpsc: {
    slug: 'tgpsc',
    name: 'TGPSC',
    title: 'TGPSC Coaching Near You - LocalAlly',
    description:
      'Find TGPSC coaching near you for Telangana state exams, general studies, current affairs and exam mentoring.',
    summary:
      'TGPSC aspirants can search for Telangana-focused mentors and coaching support using nearby discovery and registration paths.',
    subjects: ['General studies', 'Telangana state topics', 'Current affairs', 'Practice tests'],
    coachingModes: ['Mentor support', 'Local coaching', 'Online classes', 'Test practice'],
    faqs: [
      {
        question: 'Does LocalAlly support TGPSC coaching searches?',
        answer:
          'Yes. TGPSC is part of the competitive exam discovery set for Telangana aspirants.',
      },
      {
        question: 'Can TGPSC providers register?',
        answer:
          'Yes. Coaching providers and mentors can register through the tutor registration path.',
      },
    ],
  },
  'bank-exams': {
    slug: 'bank-exams',
    name: 'Bank Exams',
    title: 'Bank Exam Coaching Near You - LocalAlly',
    description:
      'Find bank exam coaching near you for quantitative aptitude, reasoning, English, general awareness and test practice.',
    summary:
      'Bank exam aspirants can search for nearby coaching support across aptitude, reasoning, English and mock-test preparation.',
    subjects: ['Quantitative aptitude', 'Reasoning', 'English', 'General awareness'],
    coachingModes: ['Group coaching', 'Online classes', 'Practice batches', 'Mentoring'],
    faqs: [
      {
        question: 'Can LocalAlly help with bank exam coaching discovery?',
        answer:
          'Yes. Bank exams are included in the competitive exam SEO and registration structure.',
      },
      {
        question: 'Can coaching providers register for bank exam batches?',
        answer:
          'Yes. Providers can register and list their exam categories, area and teaching radius.',
      },
    ],
  },
  'ssc-exams': {
    slug: 'ssc-exams',
    name: 'SSC Exams',
    title: 'SSC Exam Coaching Near You - LocalAlly',
    description:
      'Find SSC exam coaching near you for aptitude, reasoning, English, general awareness and structured practice.',
    summary:
      'LocalAlly supports SSC exam search intent with nearby coaching discovery, subject support and provider registration.',
    subjects: ['Aptitude', 'Reasoning', 'English', 'General awareness'],
    coachingModes: ['Local coaching', 'Online support', 'Practice tests', 'Mentor guidance'],
    faqs: [
      {
        question: 'Does LocalAlly include SSC exam coaching?',
        answer:
          'Yes. SSC exam coaching is part of the competitive exam pages.',
      },
      {
        question: 'Can students search by pincode?',
        answer:
          'The SEO content supports pincode and area discovery; the application flow can use those details for matching.',
      },
    ],
  },
  'railway-exams': {
    slug: 'railway-exams',
    name: 'Railway Exams',
    title: 'Railway Exam Coaching Near You - LocalAlly',
    description:
      'Find railway exam coaching near you for aptitude, reasoning, general awareness, technical basics and test practice.',
    summary:
      'Railway exam aspirants can use LocalAlly to discover coaching and mentor support near their selected area.',
    subjects: ['Aptitude', 'Reasoning', 'General awareness', 'Technical basics'],
    coachingModes: ['Local batches', 'Online classes', 'Practice support', 'Mentor guidance'],
    faqs: [
      {
        question: 'Can railway exam aspirants use LocalAlly?',
        answer:
          'Yes. Railway exams are included in the competitive exam coaching discovery plan.',
      },
      {
        question: 'Can railway exam coaches register?',
        answer:
          'Yes. Coaches can register and list their exam categories and teaching radius.',
      },
    ],
  },
  tet: {
    slug: 'tet',
    name: 'TET',
    title: 'TET Coaching Near You - LocalAlly',
    description:
      'Find TET coaching near you for teacher eligibility preparation, pedagogy, child development and subject practice.',
    summary:
      'Teacher eligibility exam aspirants can search for nearby mentors and coaching support for pedagogy, subject knowledge and practice tests.',
    subjects: ['Pedagogy', 'Child development', 'Subject practice', 'Mock tests'],
    coachingModes: ['Mentor support', 'Local coaching', 'Online classes', 'Practice sessions'],
    faqs: [
      {
        question: 'Does LocalAlly support TET coaching searches?',
        answer:
          'Yes. TET is included in the competitive exam and teacher-growth SEO plan.',
      },
      {
        question: 'Can TET mentors register?',
        answer:
          'Yes. Mentors can register through the tutor registration path and list supported exams.',
      },
    ],
  },
  dsc: {
    slug: 'dsc',
    name: 'DSC',
    title: 'DSC Coaching Near You - LocalAlly',
    description:
      'Find DSC coaching near you for teaching job exam preparation, subject revision, pedagogy and practice tests.',
    summary:
      'DSC aspirants can discover local coaching providers and mentors through LocalAlly while providers can register for student visibility.',
    subjects: ['Subject revision', 'Pedagogy', 'Practice tests', 'Exam strategy'],
    coachingModes: ['Local coaching', 'Mentor support', 'Online classes', 'Group practice'],
    faqs: [
      {
        question: 'Can LocalAlly help with DSC coaching?',
        answer:
          'Yes. DSC coaching is included in the competitive exam discovery set.',
      },
      {
        question: 'Is DSC support available only in cities?',
        answer:
          'No. LocalAlly should be positioned around nearby area and radius search, not city-only discovery.',
      },
    ],
  },
};

const NEARBY_AREA_PAGES: Record<string, NearbyAreaPage> = {
  gachibowli: {
    slug: 'gachibowli',
    name: 'Gachibowli',
    region: 'Hyderabad',
    title: 'Tutors Near Gachibowli - LocalAlly',
    description:
      'Search for tutors near Gachibowli using LocalAlly nearby discovery for all classes, subjects, boards and competitive exams.',
    summary:
      'Students and parents around Gachibowli can use LocalAlly to search by area, pincode, subject, class, board, exam and preferred radius.',
    localSignals: ['Gachibowli', 'Kondapur', 'Madhapur', 'Nanakramguda'],
    faqs: [
      {
        question: 'Does this page guarantee tutor availability in Gachibowli?',
        answer:
          'No. Availability depends on registered tutors and coaching providers. The page explains how nearby discovery should work.',
      },
      {
        question: 'Can I expand the search beyond Gachibowli?',
        answer:
          'Yes. LocalAlly discovery should support 5 km, 10 km and 20 km radius options.',
      },
    ],
  },
  kphb: {
    slug: 'kphb',
    name: 'KPHB',
    region: 'Hyderabad',
    title: 'Tutors Near KPHB - LocalAlly',
    description:
      'Search for tutors near KPHB for school classes, subjects, boards and competitive exam coaching using LocalAlly.',
    summary:
      'KPHB learners can start with nearby search and expand radius depending on the subject, class, board or exam support needed.',
    localSignals: ['KPHB', 'Kukatpally', 'Miyapur', 'Nizampet'],
    faqs: [
      {
        question: 'Can KPHB tutors register on LocalAlly?',
        answer:
          'Yes. Tutors can register, add subjects, classes, exams, area and teaching radius.',
      },
      {
        question: 'Is LocalAlly only for school tuition?',
        answer:
          'No. It also supports competitive exam coaching and skill-based learning where providers are available.',
      },
    ],
  },
  gajuwaka: {
    slug: 'gajuwaka',
    name: 'Gajuwaka',
    region: 'Vizag',
    title: 'Tutors Near Gajuwaka - LocalAlly',
    description:
      'Search for tutors near Gajuwaka for all classes, subjects, boards and exam coaching through LocalAlly nearby discovery.',
    summary:
      'The Gajuwaka nearby page explains how students and parents can use area, pincode and radius to discover education support.',
    localSignals: ['Gajuwaka', 'Malkapuram', 'Sheela Nagar', 'Kurmannapalem'],
    faqs: [
      {
        question: 'Can students search for tutors near Gajuwaka?',
        answer:
          'Yes. The intended LocalAlly flow supports nearby tutor discovery by area, pincode and radius.',
      },
      {
        question: 'Can coaching providers register?',
        answer:
          'Yes. Coaching providers can register and describe their subjects, exams and service area.',
      },
    ],
  },
  'rajahmundry-rural': {
    slug: 'rajahmundry-rural',
    name: 'Rajahmundry Rural',
    region: 'East Godavari',
    title: 'Tutors Near Rajahmundry Rural - LocalAlly',
    description:
      'Search for tutors near Rajahmundry Rural for school classes, board support and competitive exam coaching with LocalAlly.',
    summary:
      'LocalAlly should support villages, towns, mandals, colonies, districts and city areas through nearby discovery, not only metro locations.',
    localSignals: ['Rajahmundry Rural', 'Dowleswaram', 'Kadiam', 'Morampudi'],
    faqs: [
      {
        question: 'Is LocalAlly city-only?',
        answer:
          'No. The updated SEO strategy positions LocalAlly for villages, towns, mandals, colonies, districts and cities.',
      },
      {
        question: 'Can students search within 20 km?',
        answer:
          'Yes. Wider 20 km discovery is useful when specialist tutors or coaching support are not available very nearby.',
      },
    ],
  },
};

const earlyLearning = ['Nursery', 'LKG', 'UKG'];
const classNumbers = Array.from({ length: 12 }, (_, index) => index + 1);

const getClassStage = (classNumber: number) => {
  if (classNumber <= 2) return 'Early primary';
  if (classNumber <= 5) return 'Primary';
  if (classNumber <= 8) return 'Middle school';
  if (classNumber <= 10) return 'High school';
  return 'Senior secondary';
};

const buildClassPage = (classNumber: number): ClassPage => {
  const name = `Class ${classNumber}`;
  const senior = classNumber >= 11;
  const highSchool = classNumber >= 9 && classNumber <= 10;
  const primary = classNumber <= 5;

  return {
    slug: `class-${classNumber}`,
    name,
    title: `${name} Tutors Near You - LocalAlly`,
    description: `Find ${name} tutors near you for subjects, boards, homework support, exam preparation and nearby tuition through LocalAlly.`,
    h1: `Find ${name} Tutors Near You`,
    summary: `${name} students can use LocalAlly to discover nearby tutors for regular tuition, subject practice, board support and better study routines. Start with 5 km nearby discovery and expand to 10 km or 20 km when needed.`,
    subjects: senior
      ? ['Maths', 'Physics', 'Chemistry', 'Biology', 'Commerce and humanities support']
      : primary
        ? ['Maths', 'English', 'Environmental Studies', 'Reading and writing', 'Homework support']
        : ['Maths', 'Science', 'English', 'Social Studies', 'Computer basics'],
    parentGuidance: highSchool
      ? ['Plan revision early', 'Use chapter-wise practice', 'Balance school tests with board preparation', 'Track weak subjects weekly']
      : senior
        ? ['Match tutor expertise with stream', 'Plan exam targets', 'Include entrance exam foundation where needed', 'Track practice tests']
        : ['Build steady study habits', 'Correct homework gaps', 'Encourage reading and basics', 'Choose a tutor who explains patiently'],
    faqs: [
      {
        question: `Can I find tutors for ${name} on LocalAlly?`,
        answer:
          'Yes. LocalAlly is designed for all school classes, subject support and nearby tutor discovery.',
      },
      {
        question: `Is ${name} support limited to one board?`,
        answer:
          'No. Students can search for tutors by class, subject, board and area where relevant providers are available.',
      },
    ],
  };
};

const CLASS_PAGES: Record<string, ClassPage> = Object.fromEntries(
  classNumbers.map((classNumber) => {
    const page = buildClassPage(classNumber);
    return [page.slug, page];
  }),
);

const SUBJECT_LINKS: LinkItem[] = Object.values(SUBJECT_PAGES).map((subject) => ({
  label: `${subject.name} tutors`,
  to: `/education/tutors/${subject.slug}`,
  description: subject.description,
}));

const BOARD_LINKS: LinkItem[] = Object.values(BOARD_PAGES).map((board) => ({
  label: `${board.name} tutors`,
  to: `/education/boards/${board.slug}`,
  description: board.description,
}));

const CLASS_LINKS: LinkItem[] = Object.values(CLASS_PAGES).map((classPage) => ({
  label: `${classPage.name} tutors`,
  to: `/education/classes/${classPage.slug}`,
  description: `${getClassStage(Number(classPage.slug.replace('class-', '')))} tuition, subjects and nearby support.`,
}));

const EXAM_LINKS: LinkItem[] = Object.values(EXAM_PAGES).map((exam) => ({
  label: `${exam.name} coaching`,
  to: `/education/competitive-exams/${exam.slug}`,
  description: exam.description,
}));

const NEARBY_LINKS: LinkItem[] = Object.values(NEARBY_AREA_PAGES).map((area) => ({
  label: `Tutors near ${area.name}`,
  to: `/education/tutors/nearby/${area.slug}`,
  description: area.description,
}));

const EDUCATION_ENTRY_OPTIONS: LinkItem[] = [
  {
    label: 'Find a Tutor',
    to: '/student/find-teachers',
    description: 'Search nearby teachers by location, subject, curriculum, rating, fee and distance.',
  },
  {
    label: 'Register as a Tutor',
    to: '/education/tutor-registration',
    description: 'Create a tutor profile with subjects, classes, boards, location, availability and verification details.',
  },
  {
    label: 'Find Coaching',
    to: '/education/competitive-exams',
    description: 'Explore coaching support for entrance exams, government exams, school foundations and skill learning.',
  },
  {
    label: 'Book a Demo Class',
    to: '/student/find-teachers',
    description: 'Start from tutor discovery and move into selection, trial session and class confirmation.',
  },
  {
    label: 'Take an Exam',
    to: '/exams',
    description: 'Open practice tests, teacher exams, public exams and scholarship exam journeys.',
  },
  {
    label: 'Scholarships',
    to: '/scholarship',
    description: 'Register for active scholarship campaigns and view exam or result updates.',
  },
  {
    label: 'Books and Stationery',
    to: '/books',
    description: 'Buy, sell, exchange, donate, request books from shops and find verified local book shops.',
  },
  {
    label: 'Student Progress',
    to: '/student/progress',
    description: 'Review scores, subject progress, attempts, certificates and learning signals.',
  },
  {
    label: 'Parent Dashboard',
    to: '/student/dashboard',
    description: 'Track student sessions, progress, payments and tutor connections from the learning dashboard.',
  },
];

const QUICK_SUBJECT_OPTIONS = [
  { label: 'Maths', value: 'maths' },
  { label: 'Science', value: 'science' },
  { label: 'English', value: 'english' },
  { label: 'Social Studies', value: 'social-studies' },
  { label: 'Computer', value: 'computer' },
] as const;

const QUICK_CLASS_OPTIONS = [
  { label: 'Early learning', value: 'early-learning' },
  ...CLASS_LINKS.slice(0, 12).map((item) => ({
    label: item.label.replace(' tutors', ''),
    value: item.to.split('/').pop() || '',
  })),
  { label: 'Intermediate', value: 'intermediate' },
  { label: 'Competitive exam', value: 'competitive-exam' },
  { label: 'Skill-based learning', value: 'skill-learning' },
] as const;

const landingFaqs: Faq[] = [
  {
    question: 'Is LocalAlly only for Class 9 and Class 10?',
    answer:
      'No. LocalAlly supports education discovery for all classes, all subjects, boards, competitive exams and skill-based learning where tutors are available.',
  },
  {
    question: 'Is LocalAlly only for cities?',
    answer:
      'No. LocalAlly should be positioned around nearby discovery for villages, towns, mandals, colonies, neighborhoods, districts and cities.',
  },
  {
    question: 'How does nearby tutor search work?',
    answer:
      'The intended discovery model starts near the user, generally around 5 km, and can expand to 10 km or 20 km when needed.',
  },
];

const makeFaqSchema = (faqs: Faq[]) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
});

const makeBreadcrumbSchema = (items: Array<{ name: string; path: string }>) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: `${SITE_URL}${item.path}`,
  })),
});

const makeServiceSchema = ({
  name,
  description,
  path,
  serviceType = 'Education, tutoring and competitive exam coaching discovery',
  areaServed,
}: {
  name: string;
  description: string;
  path: string;
  serviceType?: string;
  areaServed?: string;
}) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name,
  serviceType,
  provider: {
    '@type': 'Organization',
    name: 'LocalAlly',
    url: SITE_URL,
  },
  areaServed: areaServed
    ? {
        '@type': 'Place',
        name: areaServed,
      }
    : {
        '@type': 'Country',
        name: 'India',
      },
  description,
  url: `${SITE_URL}${path}`,
});

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'LocalAlly',
  url: SITE_URL,
  logo: `${SITE_URL}/logo_1.jpeg`,
  description:
    'LocalAlly helps students, parents, tutors, coaching providers and local communities discover trusted people and services nearby.',
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer support',
    areaServed: 'IN',
    availableLanguage: ['English', 'Telugu'],
  },
};

const PageHero: React.FC<{
  eyebrow: string;
  title: string;
  description: string;
  primary: { label: string; to: string };
  secondary?: { label: string; to: string };
}> = ({ eyebrow, title, description, primary, secondary }) => (
  <section className="bg-white">
    <div className="mx-auto grid max-w-7xl gap-8 px-4 py-12 sm:px-6 lg:grid-cols-[1.08fr_0.92fr] lg:px-8">
      <div>
        <p className="text-sm font-semibold text-cyan-700">{eyebrow}</p>
        <h1 className="mt-4 max-w-4xl text-4xl font-semibold text-slate-950">{title}</h1>
        <p className="mt-5 max-w-3xl text-lg leading-8 text-slate-600">{description}</p>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <Link
            to={primary.to}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-cyan-700"
          >
            {primary.label}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
          {secondary && (
            <Link
              to={secondary.to}
              className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-800 transition hover:bg-slate-50"
            >
              {secondary.label}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
        <div className="grid gap-3">
          {RADIUS_OPTIONS.map((option, index) => (
            <div key={option.label} className="rounded-md border border-slate-200 bg-white p-4">
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-cyan-50 text-sm font-semibold text-cyan-700">
                  {index + 1}
                </span>
                <div>
                  <h2 className="text-base font-semibold text-slate-950">{option.label}</h2>
                  <p className="mt-1 text-sm leading-6 text-slate-600">{option.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const SectionHeading: React.FC<{
  eyebrow?: string;
  title: string;
  description?: string;
}> = ({ eyebrow, title, description }) => (
  <div className="max-w-3xl">
    {eyebrow && <p className="text-sm font-semibold text-cyan-700">{eyebrow}</p>}
    <h2 className="mt-2 text-3xl font-semibold text-slate-950">{title}</h2>
    {description && <p className="mt-3 text-base leading-7 text-slate-600">{description}</p>}
  </div>
);

const FeatureGrid: React.FC<{
  items: Array<{ icon: LucideIcon; title: string; text: string }>;
}> = ({ items }) => (
  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
    {items.map((item) => {
      const Icon = item.icon;
      return (
        <article key={item.title} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <span className="flex h-10 w-10 items-center justify-center rounded-md bg-emerald-50 text-emerald-700">
            <Icon className="h-5 w-5" aria-hidden="true" />
          </span>
          <h3 className="mt-4 text-lg font-semibold text-slate-950">{item.title}</h3>
          <p className="mt-2 text-sm leading-6 text-slate-600">{item.text}</p>
        </article>
      );
    })}
  </div>
);

const LinkGrid: React.FC<{
  title: string;
  description?: string;
  items: LinkItem[];
}> = ({ title, description, items }) => (
  <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
    <SectionHeading title={title} description={description} />
    <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {items.map((item) => (
        <Link
          key={item.to}
          to={item.to}
          className="group rounded-lg border border-slate-200 bg-white p-5 shadow-sm transition hover:border-cyan-300 hover:shadow-md"
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-slate-950">{item.label}</h3>
              <p className="mt-2 text-sm leading-6 text-slate-600">{item.description}</p>
            </div>
            <ArrowRight className="mt-1 h-5 w-5 shrink-0 text-slate-400 transition group-hover:text-cyan-700" aria-hidden="true" />
          </div>
        </Link>
      ))}
    </div>
  </section>
);

const EducationQuickSearch: React.FC = () => {
  const navigate = useNavigate();
  const [location, setLocation] = React.useState<string>('');
  const [classSlug, setClassSlug] = React.useState<string>(QUICK_CLASS_OPTIONS[0].value);
  const [subjectSlug, setSubjectSlug] = React.useState<string>(QUICK_SUBJECT_OPTIONS[0].value);
  const [teachingMode, setTeachingMode] = React.useState<string>('HOME_TUITION');
  const [radiusKm, setRadiusKm] = React.useState<string>('10');
  const [budget, setBudget] = React.useState<string>('');
  const [language, setLanguage] = React.useState<string>('');
  const [board, setBoard] = React.useState<string>('');
  const [examCategory, setExamCategory] = React.useState<string>('');

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const params = new URLSearchParams();
    params.set('location', location.trim());
    params.set('class', classSlug);
    params.set('mode', teachingMode);
    params.set('radiusKm', radiusKm);
    if (budget) params.set('budget', budget);
    if (language.trim()) params.set('language', language.trim());
    if (board) params.set('board', board);
    if (examCategory) params.set('exam', examCategory);

    const path = subjectSlug ? `/education/tutors/${subjectSlug}` : '/education/tutors';
    navigate(`${path}?${params.toString()}`);
  };

  return (
    <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="rounded-lg border border-cyan-100 bg-white p-5 shadow-sm sm:p-6">
        <SectionHeading
          eyebrow="Quick search"
          title="Find trusted tutors and coaching near you"
          description="Search by class, subject, teaching mode, nearby radius and preferences."
        />
        <form onSubmit={handleSubmit} className="mt-6 grid gap-4 lg:grid-cols-5">
          <label className="block text-sm font-medium text-slate-700 lg:col-span-2">
            Location
            <input
              value={location}
              required
              onChange={(event) => setLocation(event.target.value)}
              placeholder="Village, town, mandal, colony or city"
              className="mt-2 min-h-[44px] w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-cyan-500"
            />
          </label>
          <label className="block text-sm font-medium text-slate-700">
            Class or Level
            <select
              value={classSlug}
              required
              onChange={(event) => setClassSlug(event.target.value)}
              className="mt-2 min-h-[44px] w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-cyan-500"
            >
              {QUICK_CLASS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm font-medium text-slate-700">
            Subject
            <select
              value={subjectSlug}
              required
              onChange={(event) => setSubjectSlug(event.target.value)}
              className="mt-2 min-h-[44px] w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-cyan-500"
            >
              {QUICK_SUBJECT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm font-medium text-slate-700">
            Search Radius
            <select
              value={radiusKm}
              required
              onChange={(event) => setRadiusKm(event.target.value)}
              className="mt-2 min-h-[44px] w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-cyan-500"
            >
              {['5', '10', '15', '20'].map((value) => (
                <option key={value} value={value}>
                  {value} km
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm font-medium text-slate-700">
            Teaching Mode
            <select
              value={teachingMode}
              required
              onChange={(event) => setTeachingMode(event.target.value)}
              className="mt-2 min-h-[44px] w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-cyan-500"
            >
              <option value="HOME_TUITION">Home tuition</option>
              <option value="GROUP_TUITION">Group tuition</option>
              <option value="ONLINE">Online tuition</option>
              <option value="INSTITUTE">Institute coaching</option>
            </select>
          </label>
          <label className="block text-sm font-medium text-slate-700">
            Budget
            <select
              value={budget}
              onChange={(event) => setBudget(event.target.value)}
              className="mt-2 min-h-[44px] w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-cyan-500"
            >
              <option value="">Any budget</option>
              <option value="below-2000">Below INR 2,000</option>
              <option value="2000-5000">INR 2,000 - 5,000</option>
              <option value="5000-10000">INR 5,000 - 10,000</option>
              <option value="10000-plus">INR 10,000+</option>
            </select>
          </label>
          <label className="block text-sm font-medium text-slate-700">
            Language
            <input
              value={language}
              onChange={(event) => setLanguage(event.target.value)}
              placeholder="Telugu, English, Hindi"
              className="mt-2 min-h-[44px] w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-cyan-500"
            />
          </label>
          <label className="block text-sm font-medium text-slate-700">
            Board
            <select
              value={board}
              onChange={(event) => setBoard(event.target.value)}
              className="mt-2 min-h-[44px] w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-cyan-500"
            >
              <option value="">Any board</option>
              <option value="cbse">CBSE</option>
              <option value="state-board">State Board</option>
              <option value="icse">ICSE</option>
            </select>
          </label>
          <label className="block text-sm font-medium text-slate-700">
            Exam Category
            <select
              value={examCategory}
              onChange={(event) => setExamCategory(event.target.value)}
              className="mt-2 min-h-[44px] w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-cyan-500"
            >
              <option value="">Not exam-specific</option>
              <option value="neet">NEET</option>
              <option value="jee">JEE</option>
              <option value="eamcet">EAMCET</option>
              <option value="upsc">UPSC</option>
              <option value="tet-dsc">TET / DSC</option>
            </select>
          </label>
          <div className="flex items-end">
            <button
              type="submit"
              className="inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-md bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-cyan-700"
            >
              Search Tutors
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

const ChecklistBand: React.FC<{
  title: string;
  description: string;
  icon: LucideIcon;
  items: string[];
}> = ({ title, description, icon: Icon, items }) => (
  <section className="mx-auto grid max-w-7xl gap-6 px-4 py-10 sm:px-6 lg:grid-cols-[360px_1fr] lg:px-8">
    <div>
      <span className="flex h-11 w-11 items-center justify-center rounded-md bg-amber-50 text-amber-700">
        <Icon className="h-5 w-5" aria-hidden="true" />
      </span>
      <h2 className="mt-4 text-3xl font-semibold text-slate-950">{title}</h2>
      <p className="mt-3 text-base leading-7 text-slate-600">{description}</p>
    </div>
    <div className="grid gap-3 md:grid-cols-2">
      {items.map((item) => (
        <div key={item} className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-4">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" aria-hidden="true" />
          <p className="text-sm font-medium leading-6 text-slate-700">{item}</p>
        </div>
      ))}
    </div>
  </section>
);

const FaqSection: React.FC<{ faqs: Faq[] }> = ({ faqs }) => (
  <section className="bg-slate-50">
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
      <SectionHeading title="Common Questions" description="Answers that clarify LocalAlly scope, nearby discovery and registration paths." />
      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        {faqs.map((faq) => (
          <article key={faq.question} className="rounded-lg border border-slate-200 bg-white p-5">
            <h3 className="text-base font-semibold text-slate-950">{faq.question}</h3>
            <p className="mt-3 text-sm leading-6 text-slate-600">{faq.answer}</p>
          </article>
        ))}
      </div>
    </div>
  </section>
);

const RegistrationCta: React.FC = () => (
  <section className="bg-slate-950">
    <div className="mx-auto flex max-w-7xl flex-col gap-6 px-4 py-10 text-white sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8">
      <div>
        <p className="text-sm font-semibold text-cyan-200">Ready to continue?</p>
        <h2 className="mt-2 text-3xl font-semibold">Students, parents, tutors and coaching providers can start here.</h2>
      </div>
      <div className="flex flex-col gap-3 sm:flex-row">
        <Link
          to="/education/student-registration"
          className="inline-flex items-center justify-center gap-2 rounded-md bg-white px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-cyan-50"
        >
          Student registration
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
        <Link
          to="/education/tutor-registration"
          className="inline-flex items-center justify-center gap-2 rounded-md border border-white/20 px-5 py-3 text-sm font-semibold text-white transition hover:bg-white/10"
        >
          Tutor registration
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
      </div>
    </div>
  </section>
);

const coreSchemas = (path: string, title: string, description: string, faqs?: Faq[]) => [
  makeServiceSchema({ name: title, description, path }),
  makeBreadcrumbSchema([
    { name: 'Home', path: '/' },
    { name: 'Education', path: '/education' },
    { name: title.replace(' - LocalAlly', ''), path },
  ]),
  ...(faqs ? [makeFaqSchema(faqs)] : []),
];

export const EducationLandingPage: React.FC = () => {
  const title = 'Tutors Near You for All Classes and Competitive Exams - LocalAlly';
  const description =
    'Find nearby tutors for all school classes, boards, subjects and competitive exams. LocalAlly helps parents and students connect with trusted tutors within 5 km to 20 km.';

  return (
    <>
      <SEO
        title={title}
        description={description}
        path="/education"
        schema={[
          organizationSchema,
          makeServiceSchema({
            name: 'Nearby Tutor and Coaching Discovery Service',
            description,
            path: '/education',
          }),
          makeFaqSchema(landingFaqs),
          makeBreadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Education', path: '/education' },
          ]),
        ]}
      />
      <PageHero
        eyebrow="Education"
        title="Find trusted tutors and coaching near you"
        description="Search by class, subject, exam category, teaching mode, budget, language and distance across villages, towns, mandals, colonies, neighbourhoods and cities."
        primary={{ label: 'Find Tutor Near Me', to: '/education/tutors' }}
        secondary={{ label: 'Competitive Exam Coaching', to: '/education/competitive-exams' }}
      />
      <EducationQuickSearch />
      <LinkGrid title="Start an education journey" items={EDUCATION_ENTRY_OPTIONS} />
      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Education-first discovery"
          title="All classes, all boards, nearby search"
          description="The updated SEO strategy keeps LocalAlly broad and accurate: not only Class 9 or Class 10, and not only selected cities."
        />
        <div className="mt-6">
          <FeatureGrid
            items={[
              {
                icon: GraduationCap,
                title: 'All classes',
                text: 'Nursery, LKG, UKG, Class 1 to Class 12, intermediate and skill-based learning.',
              },
              {
                icon: BookOpen,
                title: 'Subjects and boards',
                text: 'Subject tutors and board support for school learning and regular revision.',
              },
              {
                icon: Target,
                title: 'Competitive exams',
                text: 'NEET, JEE, EAMCET, UPSC, APPSC, TGPSC, bank, SSC and more.',
              },
              {
                icon: Compass,
                title: 'Nearby radius',
                text: 'Start around 5 km, then expand to 10 km or 20 km based on availability.',
              },
            ]}
          />
        </div>
      </section>
      <LinkGrid title="Explore LocalAlly education" items={EDUCATION_CATEGORIES} />
      <ChecklistBand
        title="Tutor selection journey"
        description="LocalAlly should keep the student and parent path focused from requirement to continued learning."
        icon={CheckCircle2}
        items={[
          'Create a student or parent requirement',
          'Search nearby tutors and coaching providers',
          'Compare tutor profile, fees, badges and availability',
          'Request or confirm a demo class',
          'Select a tutor and continue into classes, progress and reviews',
          'Use books, exams and scholarship flows where they support learning',
        ]}
      />
      <LinkGrid title="Popular subject pages" items={SUBJECT_LINKS} />
      <RegistrationCta />
      <FaqSection faqs={landingFaqs} />
    </>
  );
};

export const EducationTutorsPage: React.FC = () => {
  const title = 'Home Tutors Near You for All Classes - LocalAlly';
  const description =
    'Find home tutors, private tutors, online tutors and group tuition near you for all classes, subjects, boards and exams.';
  const faqs: Faq[] = [
    {
      question: 'What can students search by?',
      answer:
        'Students and parents can search by class, subject, board, exam, area, pincode and preferred nearby radius.',
    },
    {
      question: 'Can teachers and coaching providers join?',
      answer:
        'Yes. Tutors, mentors and coaching providers can register and list subjects, classes, exams, areas and teaching radius.',
    },
  ];

  return (
    <>
      <SEO
        title={title}
        description={description}
        path="/education/tutors"
        schema={coreSchemas('/education/tutors', title, description, faqs)}
      />
      <PageHero
        eyebrow="Find tutors"
        title="Find Tutors Near You"
        description="Search for local tutors by class, subject, board, competitive exam and nearby radius. LocalAlly is built for villages, towns, mandals, colonies, neighborhoods, districts and cities."
        primary={{ label: 'Register as student', to: '/education/student-registration' }}
        secondary={{ label: 'Register as tutor', to: '/education/tutor-registration' }}
      />
      <LinkGrid title="Search by subject" items={SUBJECT_LINKS} />
      <LinkGrid title="Search by class" items={CLASS_LINKS.slice(0, 12)} />
      <LinkGrid title="Search by board" items={BOARD_LINKS} />
      <LinkGrid
        title="Nearby search examples"
        description="Nearby pages are examples of useful area landing pages. Do not create thin pages for areas without helpful content."
        items={NEARBY_LINKS}
      />
      <RegistrationCta />
      <FaqSection faqs={faqs} />
    </>
  );
};

export const TutorRegistrationSeoPage: React.FC = () => {
  const title = 'Tutor Registration Online - LocalAlly';
  const description =
    'Register as a tutor, mentor or coaching provider on LocalAlly and connect with students near your area for classes, subjects and competitive exams.';
  const faqs: Faq[] = [
    {
      question: 'Who can register as a tutor?',
      answer:
        'Teachers, home tutors, subject experts, mentors and coaching providers can register on LocalAlly.',
    },
    {
      question: 'What details should tutors add?',
      answer:
        'Tutors should add subjects, classes, boards, exams, area, pincode, preferred teaching mode and teaching radius.',
    },
  ];

  return (
    <>
      <SEO
        title={title}
        description={description}
        path="/education/tutor-registration"
        schema={coreSchemas('/education/tutor-registration', title, description, faqs)}
      />
      <PageHero
        eyebrow="Tutor registration"
        title="Register as a Local Tutor"
        description="Are you a teacher, mentor, coaching provider or subject expert? Register on LocalAlly and connect with students near your area."
        primary={{ label: 'Create tutor account', to: '/register' }}
        secondary={{ label: 'View student demand', to: '/education/tutors' }}
      />
      <ChecklistBand
        title="What tutors can list"
        description="A complete profile helps students and parents understand whether you match their learning need."
        icon={BadgeCheck}
        items={['Subjects and class levels', 'Boards and competitive exams', 'Area, pincode and teaching radius', 'Home tuition, group tuition or online mode']}
      />
      <ChecklistBand
        title="Lead quality signals"
        description="The updated SEO plan also prepares LocalAlly to track useful tutor leads by area and purpose."
        icon={Briefcase}
        items={['Student area or pincode', 'Preferred radius: 5 km, 10 km or 20 km', 'Class, subject, board or exam', 'Source campaign such as WhatsApp or Google']}
      />
      <FaqSection faqs={faqs} />
    </>
  );
};

export const StudentRegistrationSeoPage: React.FC = () => {
  const title = 'Student Registration for Nearby Tutors - LocalAlly';
  const description =
    'Students and parents can register on LocalAlly to find nearby tutors by class, subject, board, exam, area, pincode and radius.';
  const faqs: Faq[] = [
    {
      question: 'Can parents register to find tutors?',
      answer:
        'Yes. Parents can use student registration to help their child find tutor or coaching support.',
    },
    {
      question: 'What should students provide?',
      answer:
        'Useful details include class or level, board, subject, exam, area or pincode and preferred search radius.',
    },
  ];

  return (
    <>
      <SEO
        title={title}
        description={description}
        path="/education/student-registration"
        schema={coreSchemas('/education/student-registration', title, description, faqs)}
      />
      <PageHero
        eyebrow="Student registration"
        title="Find the Right Tutor Near You"
        description="Parents and students can register on LocalAlly to search for nearby tutors by subject, class, exam, board and location."
        primary={{ label: 'Create student account', to: '/register' }}
        secondary={{ label: 'Browse tutors', to: '/education/tutors' }}
      />
      <ChecklistBand
        title="What students can search for"
        description="The updated SEO scope keeps student discovery broad, local and useful."
        icon={Search}
        items={['All classes and school levels', 'Subjects, boards and homework help', 'Competitive exam coaching', 'Nearby support within 5 km, 10 km or 20 km']}
      />
      <LinkGrid title="Start with common education paths" items={[...EDUCATION_CATEGORIES, ...SUBJECT_LINKS.slice(0, 3)]} />
      <FaqSection faqs={faqs} />
    </>
  );
};

export const EducationClassesPage: React.FC = () => {
  const title = 'Tutors for All Classes Near You - LocalAlly';
  const description =
    'Find tutors near you for Nursery, LKG, UKG, Class 1 to Class 12, intermediate, board support and regular tuition.';
  const faqs: Faq[] = [
    {
      question: 'Which classes does LocalAlly support?',
      answer:
        'LocalAlly supports all school classes, from early learning through Class 12, and can expand to intermediate and skill-based learning.',
    },
    {
      question: 'Can I search by board and subject too?',
      answer:
        'Yes. Class pages link into subject and board pages so students can search more specifically.',
    },
  ];
  const earlyLearningLinks = earlyLearning.map((label) => ({
    label,
    to: '/education/classes',
    description: 'Early learning support can include reading readiness, writing basics, numbers and parent-guided routines.',
  }));

  return (
    <>
      <SEO
        title={title}
        description={description}
        path="/education/classes"
        schema={coreSchemas('/education/classes', title, description, faqs)}
      />
      <PageHero
        eyebrow="All classes"
        title="Tutors for All Classes"
        description="LocalAlly is not limited to Class 9 or Class 10. The platform supports early learning, school classes, intermediate support and skill-based learning where tutors are available."
        primary={{ label: 'Register as student', to: '/education/student-registration' }}
        secondary={{ label: 'Register as tutor', to: '/education/tutor-registration' }}
      />
      <LinkGrid title="Early learning" items={earlyLearningLinks} />
      <LinkGrid title="Class-wise tutor pages" items={CLASS_LINKS} />
      <LinkGrid title="Subjects and boards" items={[...SUBJECT_LINKS, ...BOARD_LINKS]} />
      <FaqSection faqs={faqs} />
    </>
  );
};

export const EducationClassPage: React.FC = () => {
  const { classSlug = '' } = useParams();
  const classPage = CLASS_PAGES[classSlug.toLowerCase()];

  if (!classPage) {
    return <Navigate to="/education/classes" replace />;
  }

  const path = `/education/classes/${classPage.slug}`;

  return (
    <>
      <SEO
        title={classPage.title}
        description={classPage.description}
        path={path}
        schema={coreSchemas(path, classPage.title, classPage.description, classPage.faqs)}
      />
      <PageHero
        eyebrow={getClassStage(Number(classPage.slug.replace('class-', '')))}
        title={classPage.h1}
        description={classPage.summary}
        primary={{ label: 'Register as student', to: '/education/student-registration' }}
        secondary={{ label: 'Find subject tutors', to: '/education/tutors' }}
      />
      <ChecklistBand
        title={`${classPage.name} subjects`}
        description="Class pages keep search intent practical by showing common subject needs."
        icon={BookOpen}
        items={classPage.subjects}
      />
      <ChecklistBand
        title="Parent guidance"
        description="Choose a tutor based on the student level, board, learning gaps and nearby availability."
        icon={Users}
        items={classPage.parentGuidance}
      />
      <LinkGrid title="Related pages" items={[...SUBJECT_LINKS, ...BOARD_LINKS, { label: 'Scholarship program', to: '/scholarship', description: 'Class 10-specific campaign information and exam updates.' }]} />
      <FaqSection faqs={classPage.faqs} />
    </>
  );
};

export const EducationSubjectTutorsPage: React.FC<{ subjectSlug?: string }> = ({ subjectSlug }) => {
  const params = useParams();
  const slug = (subjectSlug || params.subjectSlug || '').toLowerCase();
  const subject = SUBJECT_PAGES[slug];

  if (!subject) {
    return <Navigate to="/education/tutors" replace />;
  }

  const path = `/education/tutors/${subject.slug}`;

  return (
    <>
      <SEO
        title={subject.title}
        description={subject.description}
        path={path}
        schema={coreSchemas(path, subject.title, subject.description, subject.faqs)}
      />
      <PageHero
        eyebrow={`${subject.name} tutors`}
        title={subject.h1}
        description={subject.summary}
        primary={{ label: 'Register as student', to: '/education/student-registration' }}
        secondary={{ label: 'Register as tutor', to: '/education/tutor-registration' }}
      />
      <ChecklistBand
        title={`${subject.name} learning needs`}
        description="Subject pages help search engines and users understand the learning problems LocalAlly can organize."
        icon={Target}
        items={subject.learningNeeds}
      />
      <ChecklistBand
        title="Useful for"
        description="Students can start near their area and expand the search radius when specialist support is needed."
        icon={GraduationCap}
        items={subject.usefulFor}
      />
      <LinkGrid title="Continue by class or exam" items={[...CLASS_LINKS.slice(0, 12), ...EXAM_LINKS.slice(0, 4)]} />
      <FaqSection faqs={subject.faqs} />
    </>
  );
};

export const EducationBoardPage: React.FC = () => {
  const { boardSlug = '' } = useParams();
  const board = BOARD_PAGES[boardSlug.toLowerCase()];

  if (!board) {
    return <Navigate to="/education/classes" replace />;
  }

  const path = `/education/boards/${board.slug}`;

  return (
    <>
      <SEO
        title={board.title}
        description={board.description}
        path={path}
        schema={coreSchemas(path, board.title, board.description, board.faqs)}
      />
      <PageHero
        eyebrow={`${board.name} support`}
        title={`Find ${board.name} Tutors Near You`}
        description={board.summary}
        primary={{ label: 'Register as student', to: '/education/student-registration' }}
        secondary={{ label: 'Register as tutor', to: '/education/tutor-registration' }}
      />
      <ChecklistBand
        title={`${board.name} learning support`}
        description="Board pages connect syllabus intent with class-wise and subject-wise tutor discovery."
        icon={GraduationCap}
        items={board.support}
      />
      <LinkGrid title="Related class and subject pages" items={[...CLASS_LINKS.slice(0, 12), ...SUBJECT_LINKS]} />
      <FaqSection faqs={board.faqs} />
    </>
  );
};

export const CompetitiveExamsPage: React.FC = () => {
  const title = 'Competitive Exam Coaching Near You - LocalAlly';
  const description =
    'Discover nearby coaching and tutors for NEET, JEE, EAMCET, UPSC, APPSC, TGPSC, bank exams, SSC exams and other competitive exams.';
  const faqs: Faq[] = [
    {
      question: 'Which competitive exams does LocalAlly support?',
      answer:
        'The SEO pages include NEET, JEE, EAMCET, UPSC, APPSC, TGPSC, bank exams, SSC exams, railway exams, TET and DSC.',
    },
    {
      question: 'Can coaching providers register?',
      answer:
        'Yes. Coaching providers can register through the tutor registration path and list exam categories, subjects and service radius.',
    },
  ];

  return (
    <>
      <SEO
        title={title}
        description={description}
        path="/education/competitive-exams"
        schema={coreSchemas('/education/competitive-exams', title, description, faqs)}
      />
      <PageHero
        eyebrow="Competitive exams"
        title="Competitive Exam Coaching Near You"
        description="Find local or online coaching support for entrance exams, government exams and career-focused preparation using nearby discovery."
        primary={{ label: 'Register as student', to: '/education/student-registration' }}
        secondary={{ label: 'Register as coaching provider', to: '/education/tutor-registration' }}
      />
      <LinkGrid title="Competitive exam pages" items={EXAM_LINKS} />
      <ChecklistBand
        title="How exam discovery should work"
        description="The updated SEO plan connects exam search intent to area, pincode and radius instead of city-only pages."
        icon={Compass}
        items={['Choose exam and subject need', 'Enter area, village, town or pincode', 'Start with 5 km nearby search', 'Expand to 10 km or 20 km for specialist coaching']}
      />
      <FaqSection faqs={faqs} />
    </>
  );
};

export const CompetitiveExamPage: React.FC = () => {
  const { examSlug = '' } = useParams();
  const exam = EXAM_PAGES[examSlug.toLowerCase()];

  if (!exam) {
    return <Navigate to="/education/competitive-exams" replace />;
  }

  const path = `/education/competitive-exams/${exam.slug}`;

  return (
    <>
      <SEO
        title={exam.title}
        description={exam.description}
        path={path}
        schema={coreSchemas(path, exam.title, exam.description, exam.faqs)}
      />
      <PageHero
        eyebrow={`${exam.name} coaching`}
        title={`${exam.name} Coaching Near You`}
        description={exam.summary}
        primary={{ label: 'Register as student', to: '/education/student-registration' }}
        secondary={{ label: 'Register as provider', to: '/education/tutor-registration' }}
      />
      <ChecklistBand
        title={`${exam.name} subjects`}
        description="Exam pages keep the content specific to the preparation path."
        icon={BookOpen}
        items={exam.subjects}
      />
      <ChecklistBand
        title="Coaching options"
        description="Students can compare nearby, group and online support based on availability."
        icon={Laptop}
        items={exam.coachingModes}
      />
      <LinkGrid title="Related exam and subject pages" items={[...EXAM_LINKS.filter((item) => item.to !== path).slice(0, 6), ...SUBJECT_LINKS]} />
      <FaqSection faqs={exam.faqs} />
    </>
  );
};

export const NearbyAreaTutorsPage: React.FC = () => {
  const { areaSlug = '' } = useParams();
  const area = NEARBY_AREA_PAGES[areaSlug.toLowerCase()];

  if (!area) {
    return <Navigate to="/education/tutors" replace />;
  }

  const path = `/education/tutors/nearby/${area.slug}`;

  return (
    <>
      <SEO
        title={area.title}
        description={area.description}
        path={path}
        schema={[
          makeServiceSchema({
            name: area.title,
            description: area.description,
            path,
            areaServed: `${area.name}, ${area.region}`,
          }),
          makeFaqSchema(area.faqs),
          makeBreadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Education', path: '/education' },
            { name: 'Tutors', path: '/education/tutors' },
            { name: area.name, path },
          ]),
        ]}
      />
      <PageHero
        eyebrow={`${area.name}, ${area.region}`}
        title={`Find Tutors Near ${area.name}`}
        description={area.summary}
        primary={{ label: 'Register as student', to: '/education/student-registration' }}
        secondary={{ label: 'Register as tutor', to: '/education/tutor-registration' }}
      />
      <ChecklistBand
        title="Nearby local signals"
        description="Area pages should use real local context and avoid fake availability claims."
        icon={MapPin}
        items={area.localSignals}
      />
      <ChecklistBand
        title="Search nearby"
        description="The user can start near the selected area and expand only when needed."
        icon={Compass}
        items={RADIUS_OPTIONS.map((option) => option.label)}
      />
      <LinkGrid title="Continue with education categories" items={[...SUBJECT_LINKS, ...CLASS_LINKS.slice(0, 6), ...EXAM_LINKS.slice(0, 4)]} />
      <FaqSection faqs={area.faqs} />
    </>
  );
};
